import React, { useState, useContext, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import EnergyAnalystSummaryGrid from "./energy-analyst-summary-grid";
import EnergyAnalystDetailGrid from "./energy-analyst-detail-grid";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { SaveEnergyBankerHoursForStaffer, SaveEnergySIBankerHoursForStaffer } from "../../../../../services/api/dashboard-api";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import {
  getEditedRows,
  mergeUpdatedEnergyData,
  buildEnergyPayloadForStaffer,
  getAnalystTimeLogSummaryColumns,
  getAnalystTimeLogDetailColumns,
  handleStafferAnalystTimeLogExport,
  clearDetailStateFromStorage,
  getInitialDetailState,
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import {
  normalizeDate,
  getLastUpdateTooltip,
  submitButton
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  Button,
  Tooltip
} from "@fluentui/react-components";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import "./../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";
import { DashboardGridSkeleton } from "../../../dashboard-skeletons/dashboard-skeletons";

const EnergyAnalystDetailGridModal = ({
  show,
  onHide,
  selectedAnalyst,
  selectedAnalystData,
  fetchEnergyTechStafferTimeLog,
  setIsEnergyStafferTimeLogLoading,
  setIsEnergyStafferDealResourceLogLoading,
  fetchStafferDealDetailsandResourcingLog,
  showToast,
  loading,
  isSI = false,
  fetchEnergyJuniorStaffingIndex,
  setIsEnergyJuniorStaffingIndexLoading,
}) => {
  const hasMounted = useRef(false);
  const user = useContext(LoginContext);
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const selectedUserId = selectedAnalyst?.UserId;
  const [detailState, setDetailState] = useState({ sort: [], filter: null });
  const [originalData, setOriginalData] = useState(
    (selectedAnalystData?.Projects || []).map((p, index) => ({
      ...p,
      inEdit: true,
      _badgeIndex: index + 1,
      LastUpdatedString: normalizeDate(p.LastUpdated),
    }))
  );
  const [data, setData] = useState(originalData || []);
  const [summaryItem, setSummaryItem] = useState(selectedAnalystData);
  const [isSaving, setIsSaving] = useState(false);
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const history = useHistory();

  useEffect(() => {
    if (!hasMounted.current && user?.userID) {
      const initial = getInitialDetailState(user.userID, "analystDealsTimeLog");
      setDetailState(initial);
      hasMounted.current = true;
    }
  }, [user?.userID]);

  useEffect(() => {
    const newOriginalData = (selectedAnalystData?.Projects || []).map((p, index) => ({
      ...p,
      inEdit: true,
      _badgeIndex: index + 1,
      LastUpdatedString: normalizeDate(p.LastUpdated),
    }));
    setOriginalData(newOriginalData);
    setData(newOriginalData);
    setSummaryItem(selectedAnalystData);
  }, [selectedAnalystData]);

  useEffect(() => {
    document.body.classList.toggle("energy-dialog-open", show);
  }, [show]);

  const handleItemChange = (e) => {
    const { field, value, dataItem } = e;
    if (value < 0 && value !== null) return;
    const updated = data.map((row) =>
      row.ProjectId === dataItem.ProjectId ? { ...row, [field]: value } : row
    );
    setData(updated);
  };

  const handleSubmit = async () => {
    const editedRows = getEditedRows(data, originalData);
    if (editedRows.length === 0) return;

    const payload = buildEnergyPayloadForStaffer(editedRows, selectedUserId);
    setIsSaving(true);
    setIsEnergyStafferTimeLogLoading(true);
    setIsEnergyStafferDealResourceLogLoading(true);
    setIsEnergyJuniorStaffingIndexLoading(true);

    try {
      const saveEndpoint = isSI ? SaveEnergySIBankerHoursForStaffer : SaveEnergyBankerHoursForStaffer;
      const response = await saveEndpoint(payload);
      const updated = response?.data;

      if (!updated?.Projects?.length) {
        showToast("Save Failed", "Could not save your changes.", "error");
        setIsSaving(false);
        setIsEnergyStafferTimeLogLoading(false);
        setIsEnergyStafferDealResourceLogLoading(false);
        setIsEnergyJuniorStaffingIndexLoading(false);
        return;
      } else {
        const merged = mergeUpdatedEnergyData(updated.Projects);
        setData(merged);
        setOriginalData(merged);
        setSummaryItem(updated);

        if (!response?.data) {
          showToast("Save Failed", "Could not save your changes.", "error");
          setIsSaving(false);
          setIsEnergyStafferTimeLogLoading(false);
          setIsEnergyStafferDealResourceLogLoading(false);
          setIsEnergyJuniorStaffingIndexLoading(false);
        } else {
          const now = new Date();
          setIsSaving(false);
          showToast(
            "Edits Saved Successfully",
            `Changes saved on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            })}`,
            "success"
          );
          fetchEnergyTechStafferTimeLog()
            .then(() => {
              setIsEnergyStafferTimeLogLoading(false)
            });
          fetchStafferDealDetailsandResourcingLog()
            .then(() => {
              setIsEnergyStafferDealResourceLogLoading(false)
            });
          fetchEnergyJuniorStaffingIndex()
            .then(() => {
              setIsEnergyJuniorStaffingIndexLoading(false)
            });
        }
      }
    } catch (err) {
      console.error("Failed to submit:", err);
      showToast(
        "Save Failed",
        "An unexpected error occurred. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
      setIsEnergyStafferTimeLogLoading(false);
      setIsEnergyStafferDealResourceLogLoading(false);
      setIsEnergyJuniorStaffingIndexLoading(false);
    }
  };

  const editedRows = getEditedRows(data, originalData);
  const isSubmitDisabled = isSaving || editedRows?.length === 0;
  const lastUpdated = summaryItem?.LastUpdated;
  const tooltipText = getLastUpdateTooltip(lastUpdated);
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);

  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;

  const resetDataState = () => {
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
    clearDetailStateFromStorage(user?.userID, "analystDealsTimeLog");
  };

  return (
    <div>
      <Dialog open={show} modalType="modal"
        onOpenChange={(event, data) => {
          if (!data.open) {
            onHide();
          }
        }}>
        <DialogSurface aria-describedby={undefined} className="analyst-time-tracker-modal">
          {loading ?
            <DashboardGridSkeleton width="100%" noShadow={true} />
            :
            <DialogBody>
              <DialogTitle className="d-flex justify-content-between "
                action={
                  <DialogTrigger action="close">
                    <Button
                      appearance="subtle"
                      aria-label="close"
                      icon={<FluentUiIcon name="DismissRegular" />}
                    />
                  </DialogTrigger>
                }
              >
                <div>
                  {selectedAnalyst?.GroupName && (
                    <FluentBadge
                      shape="rounded"
                      appearance="tint"
                      size="medium"
                      color="brand"
                      text={selectedAnalyst.GroupName}
                    />
                  )}
                  <div className="modal-deal-title-text">{selectedAnalyst?.Name}</div>
                  <p className="subDepartmentTitle">Analyst & Associate Time Log</p>
                </div>
                <div className="d-flex align-items-start">
                  <FluentTooltip content="Reset Filters and Sorting" target={
                    <FluentButton
                      appearance="secondaryWithOutline"
                      icon={
                        <FluentUiIcon
                          name="ArrowResetRegular"
                          color={canReset ? "#0F6CBD" : "#A0A0A0"}
                          size={20}
                          disabled={!canReset}
                        />
                      }
                      disabled={!canReset}
                      onClick={resetDataState}
                    />
                  } />
                  <span className={"ml-2"}>
                    <FluentTooltip content="Export Data" target={
                      <FluentButton
                        appearance="secondaryWithOutline"
                        icon={
                          <FluentUiIcon
                            name="ArrowDownloadRegular"
                            color="#0F6CBD"
                            size={20}
                          />
                        }
                        onClick={() =>
                          handleStafferAnalystTimeLogExport({ summaryRef: _summaryExport, detailRef: _detailExport })
                        }
                      />
                    } />
                  </span>
                  <span className={"ml-2"}>
                    {tooltipText
                      ? <Tooltip content={tooltipText} relationship="label" positioning="below">
                        <div>{submitBtn}</div>
                      </Tooltip>
                      : submitBtn
                    }
                  </span>
                </div>
              </DialogTitle>
              <DialogContent>
                <div className="energy-banker-time-log noBorder" style={{ maxWidth: "100%" }}>
                  <ExcelExport data={[summaryItem]} ref={_summaryExport}>
                    {getAnalystTimeLogSummaryColumns()}
                  </ExcelExport>
                  <EnergyAnalystSummaryGrid
                    summaryItem={summaryItem}
                    showDetails={true}
                  //onToggle={() => setShowDetails(!showDetails)}
                  />
                  <ExcelExport data={data} ref={_detailExport}>
                    {getAnalystTimeLogDetailColumns()}
                  </ExcelExport>
                  <EnergyAnalystDetailGrid
                    data={data}
                    onItemChange={handleItemChange}
                    detailState={detailState}
                    onDetailStateChange={setDetailState}
                    resetDataState={resetDataState}
                  />
                </div>
              </DialogContent>
            </DialogBody>
          }
        </DialogSurface>
      </Dialog>
    </div>
  );
};

EnergyAnalystDetailGridModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedAnalyst: PropTypes.shape({
    UserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  selectedAnalystData: PropTypes.shape({
    Projects: PropTypes.arrayOf(
      PropTypes.shape({
        ProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        ProjectName: PropTypes.string,
        LastWeekHours: PropTypes.number,
        WeekendHours: PropTypes.number,
        TotalHours: PropTypes.number,
        CurrentWeekHours: PropTypes.number,
        LastUpdated: PropTypes.string,
      })
    ).isRequired,
    LastUpdated: PropTypes.string,
    AllDealsHours: PropTypes.number,
    TotalLastWeekHours: PropTypes.number,
    TotalWeekendHours: PropTypes.number,
    TotalCurrentWeekHours: PropTypes.number,
    User: PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      Name: PropTypes.string,
    }),
  }).isRequired,
  fetchEnergyTechStafferTimeLog: PropTypes.func,
  setIsEnergyStafferTimeLogLoading: PropTypes.func,
  setIsEnergyStafferDealResourceLogLoading: PropTypes.func,
  fetchStafferDealDetailsandResourcingLog: PropTypes.func,
  showToast: PropTypes.func,
  loading: PropTypes.bool,
  isSI: PropTypes.bool,
  fetchEnergyJuniorStaffingIndex: PropTypes.func,
  setIsEnergyJuniorStaffingIndexLoading: PropTypes.func,
};

export default EnergyAnalystDetailGridModal;
